<template>
	<div class="container">
		<Loading v-if="syncing || load" />
		<div class="dashboard-header">
			<h2 class="h2">Dashboard</h2>
			<div class="action-buttons">
				<router-link to="/cadastrar" class="btn btn-primary">
					Novo Lead
				</router-link>
				<router-link to="/definir-local" class="btn btn-primary">
					Definir local
				</router-link>
				<button @click="sincronizar" class="btn btn-success" :disabled="!online || syncing">
					{{ syncing ? "Sincronizando..." : "Enviar para LeadCasting" }}
				</button>
				<router-link to="/extrato" class="btn btn-primary">
					Extrato
				</router-link>
			</div>
		</div>

		<div class="stats-cards">
			<div class="stat-card">
				<h3>{{ leadsDay }}</h3>
				<p>Total de leads do dia</p>
			</div>
			<div class="stat-card">
				<h3>{{ leadsNaoEnviados }}</h3>
				<p>Aguardando Sincronização</p>
			</div>
		</div>

		<div class="card">
			<h3>Leads Cadastrados</h3>
			<div class="table-responsive">
				<table class="candidates-table">
					<thead>
						<tr>
							<th>Nome</th>
							<th>Idade</th>
							<th>Telefone</th>
							<th>Responsável</th>
							<th>Data</th>
							<th>Status</th>
							<th>Editar</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="(lead, index) in leads" :key="index">
							<td>{{ lead.nome }}</td>
							<td>{{ lead.idade }}</td>
							<td>{{ lead.telefone }}</td>
							<td>{{ lead.responsavel || " - " }}</td>
							<td>{{ formatDate(lead.created_at) }}</td>
							<td>
								<span class="status" :class="{ enviado: lead.enviado_bubble }">
									{{ lead.enviado_bubble ? "Enviado" : "Pendente" }}
								</span>
							</td>
							<td>
								<router-link :to="'/editar/' + lead.id" class="icon-editar" title="Editar lead">
									<i class="bi bi-pencil-square"></i>
								</router-link>
								<span class="icon-delete" @click="removeLead(lead.id)">
									<i class="bi bi-x-circle" title="Apagar lead"></i>
								</span>
							</td>
						</tr>
					</tbody>
				</table>

				<div v-if="leads.length == 0" class="empty-state">
					<p class="margin-bottom-40px">Nenhum lead cadastrado ainda.</p>
					<router-link to="/cadastrar" class="btn btn-primary">
						Cadastrar Primeiro Lead
					</router-link>
				</div>
			</div>
		</div>

		<div class="success" v-if="syncMessage">
			{{ syncMessage }}
			<button v-if="syncResults" @click="showDetailsModal = true"
				style="margin-left: 10px; text-decoration: underline; background: none; border: none; color: inherit; cursor: pointer;">
				Ver detalhes
			</button>
		</div>

		<div v-if="showDetailsModal && syncResults" class="modal-overlay" @click="fecharModal">
			<div class="modal-content" @click.stop>
				<div class="modal-header">
					<h3>📊 Relatório de Sincronização</h3>
					<button @click="fecharModal" class="close-btn">✕</button>
				</div>

				<div class="modal-body">
					<div class="summary">
						<div class="summary-item success">
							<span class="label">✅ Sucesso</span>
							<span class="value">{{ syncResults.success.length }}</span>
						</div>
						<div class="summary-item warning">
							<span class="label">⚠️ Duplicados</span>
							<span class="value">{{ syncResults.duplicates.length }}</span>
						</div>
						<div class="summary-item error">
							<span class="label">❌ Com erro</span>
							<span class="value">{{ syncResults.errors.length }}</span>
						</div>
					</div>

					<div v-if="syncResults.errors.length > 0" class="errors-section">
						<h4>Erros Encontrados:</h4>
						<div class="error-list">
							<div v-for="(erro, index) in syncResults.errors" :key="index" class="error-item">
								<div class="error-info">
									<strong>{{ erro.nome }}</strong>
									<span class="error-phone">{{ erro.telefone }}</span>
								</div>
								<div class="error-message">{{ erro.error }}</div>
							</div>
						</div>
					</div>

					<div v-if="syncResults.duplicates.length > 0" class="duplicates-section">
						<h4>Duplicados Ignorados:</h4>
						<div class="duplicate-list">
							<div v-for="(dup, index) in syncResults.duplicates" :key="index" class="duplicate-item">
								<strong>{{ dup.nome }}</strong>
								<span>{{ dup.telefone }}</span>
							</div>
						</div>
					</div>
				</div>

				<div class="modal-footer">
					<button @click="router.push('/extrato')" class="retry-btn">Ver extrato</button>
					<button @click="fecharModal" class="ok-btn">OK</button>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup>
import { onBeforeMount, onMounted, computed, ref, reactive } from "vue";
import Loading from "../components/Loading.vue";
import { api } from "../services/api";
import { useRouter } from "vue-router";

const router = useRouter();

const leads = ref([]);
const leadsDay = ref(0);
const syncing = ref(false);
const syncMessage = ref("");
const online = ref(navigator.onLine);
const load = ref(false);

const leadsNaoEnviados = computed(() => {
	return leads.value.filter((c) => !c.enviado_bubble).length;
})

const formatDate = (dateString) => {
	return new Date(dateString).toLocaleDateString("pt-BR");
}

const updateOnlineStatus = () => {
	online.value = navigator.onLine;
}

onMounted(async () => {
	const usuario = JSON.parse(localStorage.getItem('usuario'));
	if (usuario.perfil === "Admin") {
		router.push({ name: "Admin" });
		return
	}
	await carregarLeads();
	await LeadsDay();
	window.addEventListener("online", updateOnlineStatus);
	window.addEventListener("offline", updateOnlineStatus);
});

onBeforeMount(() => {
	window.removeEventListener("online", updateOnlineStatus);
	window.removeEventListener("offline", updateOnlineStatus);

});

function carregarLeads() {
	const offlineData = localStorage.getItem("leads_offline");

	if (offlineData) {
		leads.value = JSON.parse(offlineData);
	}
}

function removeLead(id) {
	leads.value = leads.value.filter(lead => lead.id !== id);
	localStorage.setItem('leads_offline', JSON.stringify(leads.value));
}

async function LeadsDay() {
	try {
		load.value = true;
		leadsDay.value = await api.getLeadsDay();
	} catch (error) {
		console.error("Erro ao carregar leads do dia:", error);
	} finally {
		load.value = false;
	}
}

const syncResults = ref(null);
const showDetailsModal = ref(false);

async function sincronizar() {
	if (!online.value) {
		alert("Você precisa estar online para sincronizar");
		return;
	}

	const leadsOff = localStorage.getItem("leads_offline");
	if (!leadsOff || JSON.parse(leadsOff).length == 0) return;

	syncing.value = true;
	syncMessage.value = "Sincronizando...";

	try {
		const results = await api.cadastrarLeads(leadsOff);

		console.log('📊 Resultados recebidos:', results);

		syncResults.value = results;

		const total = results.success.length + results.duplicates.length + results.errors.length;

		await LeadsDay();

		// ✅ Remove APENAS os que tiveram sucesso (foram salvos no banco)
		if (results.success.length > 0) {
			const leadsArray = JSON.parse(leadsOff);

			// Índices apenas dos sucessos (em ordem decrescente)
			const indicesSucesso = results.success
				.map(r => r.index)
				.sort((a, b) => b - a);

			console.log('📦 Leads antes:', leadsArray.length);
			console.log('✅ Índices de sucesso a remover:', indicesSucesso);

			// Remove apenas os sucessos
			indicesSucesso.forEach(index => {
				leadsArray.splice(index, 1);
			});

			console.log('📦 Leads depois:', leadsArray.length);
			console.log('⚠️ Duplicados mantidos:', results.duplicates.length);
			console.log('❌ Erros mantidos:', results.errors.length);

			// Atualiza localStorage e estado
			if (leadsArray.length === 0) {
				localStorage.removeItem("leads_offline");
				leads.value = [];
			} else {
				localStorage.setItem("leads_offline", JSON.stringify(leadsArray));
				leads.value = leadsArray;
			}
		}

		// Mensagens baseadas no resultado
		if (results.errors.length === 0 && results.duplicates.length === 0) {
			syncMessage.value = `✅ ${total} leads sincronizados com sucesso!`;
		} else if (results.success.length > 0) {
			syncMessage.value = `⚠️ ${results.success.length} enviados, ${results.duplicates.length} duplicados, ${results.errors.length} erros`;
		} else {
			syncMessage.value = `❌ Nenhum lead foi sincronizado. ${results.duplicates.length} duplicados, ${results.errors.length} erros`;
		}

		// Sempre abre modal para mostrar detalhes
		showDetailsModal.value = true;

		console.log('🔔 Modal deve abrir:', showDetailsModal.value);

	} catch (error) {
		console.error('❌ Erro na sincronização:', error);
		syncMessage.value = "Erro na sincronização: " + (error.response?.data?.error || error.message);
	} finally {
		syncing.value = false;
		setTimeout(() => {
			if (!showDetailsModal.value) {
				syncMessage.value = "";
			}
		}, 5000);
	}
}

function fecharModal() {
	showDetailsModal.value = false;
	syncMessage.value = "";
}

// async function sincronizar() {
// 	if (!online) {
// 		alert("Você precisa estar online para sincronizar");
// 		return;
// 	}

// 	const leadsOff = localStorage.getItem("leads_offline");
// 	if (!leadsOff || leadsOff.length == 0) return;

// 	syncing.value = true;
// 	syncMessage.value = "";

// 	try {
// 		await api.cadastrarLeads(localStorage.getItem("leads_offline"));
// 		await LeadsDay();
// 		localStorage.removeItem("leads_offline");
// 		syncMessage.value = "Leads sincronizados com sucesso!";
// 		leads.value = [];
// 	} catch (error) {
// 		syncMessage.value =
// 			"Erro na sincronização: " +
// 			(error.response?.data?.error || error.message);
// 		console.log("Error", error);
// 	} finally {
// 		syncing.value = false;
// 		setTimeout(() => (syncMessage.value = ""), 5000);
// 	}
// }
</script>


<style scoped>
.modal-overlay {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: rgba(0, 0, 0, 0.6);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 9999;
}

.modal-content {
	background: white;
	border-radius: 12px;
	max-width: 600px;
	width: 90%;
	max-height: 80vh;
	overflow: hidden;
	box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
}

.modal-header {
	padding: 20px;
	border-bottom: 1px solid #e0e0e0;
	display: flex;
	justify-content: space-between;
	align-items: center;
}

.modal-header h3 {
	margin: 0;
	font-size: 20px;
}

.close-btn {
	background: none;
	border: none;
	font-size: 28px;
	cursor: pointer;
	color: #666;
	width: 32px;
	height: 32px;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
}

.close-btn:hover {
	background: #f0f0f0;
}

.modal-body {
	padding: 20px;
	overflow-y: auto;
	max-height: 60vh;
}

.summary {
	display: flex;
	gap: 12px;
	margin-bottom: 24px;
	justify-content: center;
	align-items: baseline;
}

.summary-item {
	padding: 20px;
	border-radius: 8px;
	text-align: center;
	width: 100%;
}

.summary-item.success {
	background: #e8f5e9;
	color: #2e7d32;
}

.summary-item.warning {
	background: #fff3e0;
	color: #e65100;
}

.summary-item.error {
	background: #ffebee;
	color: #c62828;
}

.summary-item .label {
	display: block;
	font-size: 14px;
	margin-bottom: 8px;
}

.summary-item .value {
	display: block;
	font-size: 36px;
	font-weight: bold;
}

.errors-section,
.duplicates-section {
	margin-top: 24px;
}

.errors-section h4,
.duplicates-section h4 {
	margin: 0 0 12px 0;
}

.error-list,
.duplicate-list {
	max-height: 250px;
	overflow-y: auto;
}

.error-item {
	padding: 12px;
	background: #fff5f5;
	border-left: 4px solid #f44336;
	border-radius: 4px;
	margin-bottom: 8px;
}

.error-info {
	display: flex;
	justify-content: space-between;
	margin-bottom: 4px;
}

.error-phone {
	color: #666;
	font-size: 14px;
}

.error-message {
	font-size: 13px;
	color: #d32f2f;
	font-style: italic;
}

.duplicate-item {
	padding: 12px;
	background: #fffbf0;
	border-left: 4px solid #ff9800;
	border-radius: 4px;
	margin-bottom: 8px;
	display: flex;
	justify-content: space-between;
}

.modal-footer {
	padding: 20px;
	border-top: 1px solid #e0e0e0;
	display: flex;
	justify-content: flex-end;
	gap: 12px;
}

.retry-btn,
.ok-btn {
	padding: 10px 20px;
	border: none;
	border-radius: 6px;
	font-weight: 600;
	cursor: pointer;
}

.retry-btn {
	background: #2196F3;
	color: white;
}

.retry-btn:hover {
	background: #1976D2;
}

.ok-btn {
	background: #4CAF50;
	color: white;
}

.ok-btn:hover {
	background: #388E3C;
}
</style>